import React, { useState, useEffect, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChevronDown, faChevronUp, faComments } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { formatDateTime, SUPPORT_LEVEL_OPTIONS } from './utils';

const SidebarTicketHistory = ({ currentTicketId, historyRefreshKey = 0, ticket, onOpenCombinedMessages }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token } = useToken();
  const [ticketHistory, setTicketHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Detalle desplegable de tickets y sus movimientos
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [ticketEventsMap, setTicketEventsMap] = useState({});
  const [isLoadingEventsMap, setIsLoadingEventsMap] = useState({});

  // Cargar eventos del ticket desde la base de datos (tblTicketsHistorial)
  const fetchTicketEvents = async idTicket => {
    if (!idTicket || !token) return;
    setIsLoadingEventsMap(prev => ({ ...prev, [idTicket]: true }));
    try {
      const response = await fetch(`/api/SupportEngine/GetTicketEvents?idTicket=${idTicket}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });
      if (response.ok) {
        const result = await response.json();
        const list = result.data || result;
        setTicketEventsMap(prev => ({ ...prev, [idTicket]: Array.isArray(list) ? list : [] }));
      } else {
        setTicketEventsMap(prev => ({ ...prev, [idTicket]: [] }));
      }
    } catch (err) {
      console.error('Error al cargar eventos del ticket:', err);
      setTicketEventsMap(prev => ({ ...prev, [idTicket]: [] }));
    } finally {
      setIsLoadingEventsMap(prev => ({ ...prev, [idTicket]: false }));
    }
  };

  const handleToggleTicketDetails = idTicket => {
    if (expandedTicketId === idTicket) {
      setExpandedTicketId(null);
      return;
    }

    setExpandedTicketId(idTicket);
    fetchTicketEvents(idTicket);
  };

  // Auto-expandir el ticket actual por defecto para mostrar de inmediato sus movimientos
  useEffect(() => {
    if (currentTicketId && !expandedTicketId) {
      const numTicketId = parseInt(currentTicketId);
      setExpandedTicketId(numTicketId);
      fetchTicketEvents(numTicketId);
    }
  }, [currentTicketId]);

  // Cargar Historial de tickets directamente desde la BD por idTicket
  useEffect(() => {
    const fetchHistory = async () => {
      const ticketId = currentTicketId || ticket?.idTicket || ticket?.id;
      if (!ticketId) {
        setTicketHistory([]);
        return;
      }

      setIsLoadingHistory(true);
      try {
        const response = await fetch(`/api/SupportEngine/GetTicketHistory?idTicket=${ticketId}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + token
          }
        });
        if (response.ok) {
          const result = await response.json();
          const list = result.data || result;
          setTicketHistory(Array.isArray(list) ? list : []);
        } else {
          setTicketHistory([]);
        }
      } catch (err) {
        console.error('Error al cargar historial de tickets:', err);
        setTicketHistory([]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchHistory();
  }, [currentTicketId, token, historyRefreshKey]);

  // Refrescar eventos del ticket cuando se realiza una acción o reapertura (cierre, asignación, mensaje nuevo)
  useEffect(() => {
    const targetId = expandedTicketId || (currentTicketId ? parseInt(currentTicketId) : null);
    if (targetId) {
      fetchTicketEvents(targetId);
    }
  }, [historyRefreshKey]);

  // Al cambiar de ticket, resetear eventos y acordeones
  useEffect(() => {
    setTicketEventsMap({});
    setExpandedTicketId(null);
  }, [currentTicketId]);

  return (
    <div>
      <div
        className="fw-semibold mb-2 pb-1 d-flex align-items-center justify-content-between"
        style={{ fontSize: '0.68rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b' }}
      >
        <span>HISTORIAL DE TICKETS</span>
        {isLoadingHistory && (
          <span className="d-inline-flex align-items-center gap-1 text-primary fw-medium" style={{ fontSize: '0.65rem', textTransform: 'none' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.62rem' }} />
            <span>Actualizando...</span>
          </span>
        )}
      </div>

      {isLoadingHistory && ticketHistory.length === 0 ? (
        <div className="text-center py-2 text-muted" style={{ fontSize: '0.75rem' }}>
          <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-primary" />
          Cargando historial...
        </div>
      ) : ticketHistory.length === 0 ? (
        <div
          className="p-2 rounded border text-center"
          style={{
            fontSize: '0.75rem',
            backgroundColor: isDark ? '#0b1727' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0',
            color: isDark ? '#94a3b8' : '#64748b'
          }}
        >
          No hay tickets registrados.
        </div>
      ) : (
        <div className="position-relative ps-3">
          {/* Línea vertical de la línea de tiempo */}
          <div
            className="position-absolute"
            style={{
              left: '7px',
              top: '7px',
              bottom: '12px',
              width: '1.5px',
              backgroundColor: isDark ? '#233142' : '#cbd5e1'
            }}
          />

          {ticketHistory.map((item, idx) => {
            const isCurrent = item.idTicket === parseInt(currentTicketId);
            const isAbierto = item.iEstadoTicket === 1;
            const isEnProgreso = item.iEstadoTicket === 3;
            const isExpanded = expandedTicketId === item.idTicket;
            const events = ticketEventsMap[item.idTicket] || [];
            const isLoadingEvents = isLoadingEventsMap[item.idTicket];

            return (
              <div key={`hist-${item.idTicket}-${idx}`} className="position-relative mb-3 pb-1">
                {/* Nodo circular del timeline */}
                {isAbierto ? (
                  <div
                    className="position-absolute rounded-circle"
                    style={{
                      left: '-14px',
                      top: '3px',
                      width: '12px',
                      height: '12px',
                      border: '2.5px solid #f59e0b',
                      backgroundColor: isDark ? '#0b141a' : '#ffffff'
                    }}
                  />
                ) : isEnProgreso ? (
                  <div
                    className="position-absolute rounded-circle"
                    style={{
                      left: '-14px',
                      top: '3px',
                      width: '12px',
                      height: '12px',
                      border: '2.5px solid #0ea5e9',
                      backgroundColor: isDark ? '#0b141a' : '#ffffff'
                    }}
                  />
                ) : (
                  <div
                    className="position-absolute rounded-circle"
                    style={{
                      left: '-13px',
                      top: '4px',
                      width: '10px',
                      height: '10px',
                      backgroundColor: isDark ? '#475569' : '#9ca3af'
                    }}
                  />
                )}

                {/* Tarjeta clickeable del ticket */}
                <div
                  className="p-1 rounded cursor-pointer transition-all hover-bg-200"
                  onClick={() => handleToggleTicketDetails(item.idTicket)}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '6px'
                  }}
                  title="Clic para ver/ocultar detalles de este ticket"
                >
                  {/* Fila 1: Fecha y Badge Actual / Chevron */}
                  <div className="d-flex align-items-center justify-content-between">
                    <small style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#52525b', fontWeight: 500 }}>
                      {formatDateTime(item.dCreatedDate)}
                    </small>
                    <div className="d-flex align-items-center gap-1">
                      {isCurrent && (
                        <span
                          className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25"
                          style={{ fontSize: '0.60rem', padding: '1px 5px' }}
                        >
                          Actual
                        </span>
                      )}
                      <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className="text-muted"
                        style={{ fontSize: '0.62rem' }}
                      />
                    </div>
                  </div>

                  {/* Fila 2: Título (Ticket #1 En progreso / Resuelto) */}
                  <div
                    style={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      lineHeight: '1.25',
                      color: isDark ? '#f9fafd' : '#0f172a'
                    }}
                  >
                    Ticket #{item.idTicket} {item.vEstadoDesc || (isAbierto ? 'Pendiente' : isEnProgreso ? 'En Progreso' : 'Cerrado')}
                  </div>

                  {/* Fila 3: Subtítulo (Vía WhatsApp o Asunto) */}
                  <div
                    style={{
                      fontSize: '0.74rem',
                      color: isDark ? '#94a3b8' : '#64748b',
                      lineHeight: '1.2'
                    }}
                  >
                    {item.vAsunto || 'Vía WhatsApp'}
                  </div>

                  {/* Fila 4: Nivel de soporte y Creado por */}
                  <div
                    className="d-flex align-items-center justify-content-between mt-1 pt-1 border-top"
                    style={{ fontSize: '0.69rem', color: isDark ? '#cbd5e1' : '#0f172a', borderColor: isDark ? '#233142' : '#e2e8f0' }}
                  >
                    <span>
                      {SUPPORT_LEVEL_OPTIONS.find(o => o.value === item.idCatNivelSoporte)?.label || (item.idCatNivelSoporte ? `Nivel ${item.idCatNivelSoporte}` : 'Nivel 1 - Basico')}
                    </span>
                    {item.vCreatedUser && (
                      <span>
                        Por: <span className="fw-semibold">{item.vCreatedUser}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Panel desplegable con movimientos del ticket al hacer clic */}
                {isExpanded && (
                  <div
                    className={`mt-2 p-2 rounded-2 border se-custom-scrollbar ${isDark ? 'se-dark-scrollbar' : ''}`}
                    style={{
                      backgroundColor: isDark ? '#0b1727' : '#f8fafc',
                      borderColor: isDark ? '#233142' : '#e2e8f0',
                      fontSize: '0.74rem',
                      animation: 'fadeIn 0.2s ease-in',
                      maxHeight: '280px',
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                      scrollbarColor: isDark ? '#2b394a transparent' : '#cbd5e1 transparent'
                    }}
                  >
                    {/* Fecha de cierre si el ticket está cerrado */}
                    {item.dFechaCierre && !isAbierto && (
                      <div
                        className="d-flex justify-content-between text-muted pb-1 mb-2 border-bottom"
                        style={{ fontSize: '0.70rem', borderColor: isDark ? '#233142' : '#e2e8f0' }}
                      >
                        <span>Cerrado:</span>
                        <span style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{formatDateTime(item.dFechaCierre)}</span>
                      </div>
                    )}

                    {/* Eventos / Bitácora del ticket */}
                    <div
                      className="fw-semibold mb-2 d-flex align-items-center justify-content-between"
                      style={{ fontSize: '0.68rem', letterSpacing: '0.03em', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b' }}
                    >
                      <span>MOVIMIENTOS DEL TICKET</span>
                      {isLoadingEvents && <FontAwesomeIcon icon={faSpinner} spin className="text-primary" />}
                    </div>

                    {isLoadingEvents ? (
                      <div className="text-center py-2 text-muted" style={{ fontSize: '0.72rem' }}>
                        <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-primary" />
                        Cargando movimientos...
                      </div>
                    ) : events.length === 0 ? (
                      <div className="text-muted text-center py-1 fst-italic" style={{ fontSize: '0.70rem' }}>
                        Sin movimientos registrados.
                      </div>
                    ) : (
                      <div className="position-relative ps-2 my-1" style={{ borderLeft: isDark ? '1.5px solid #233142' : '1.5px solid #cbd5e1' }}>
                        {[...events]
                          .sort((a, b) => (b.idTicketHistorial || 0) - (a.idTicketHistorial || 0))
                          .map(ev => {
                          const isCierreEv = ev.vTipoEvento === 'CIERRE';
                          const isCreacionEv = ev.vTipoEvento === 'CREACION';
                          const isReaperturaEv = ev.vTipoEvento === 'REAPERTURA';
                          const isCombinadoEv = ev.vTipoEvento === 'COMBINACION_TICKETS';

                          return (
                            <div key={ev.idTicketHistorial} className="mb-2 ps-2 position-relative">
                              <div
                                className="position-absolute rounded-circle"
                                style={{
                                  left: '-12px',
                                  top: '4px',
                                  width: '6px',
                                  height: '6px',
                                  backgroundColor: isCierreEv ? '#ef4444' : isReaperturaEv ? '#f59e0b' : isCreacionEv ? '#10b981' : isCombinadoEv ? '#a855f7' : '#3b82f6'
                                }}
                              />
                              <div className="d-flex align-items-center justify-content-between">
                                <span className="fw-semibold" style={{ fontSize: '0.72rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                  {ev.vTitulo}
                                </span>
                                <span className="text-muted" style={{ fontSize: '0.65rem' }}>
                                  {formatDateTime(ev.dCreatedDate)}
                                </span>
                              </div>
                              {ev.vDescripcion && (
                                <div className="text-muted" style={{ fontSize: '0.69rem' }}>
                                  {ev.vDescripcion}
                                </div>
                              )}
                              {isCombinadoEv && ev.vDescripcion && (() => {
                                const match = ev.vDescripcion.match(/#(\d+)/);
                                const srcTicketId = match ? Number(match[1]) : null;
                                if (!srcTicketId || srcTicketId === item.idTicket) return null;
                                return (
                                  <button
                                    type="button"
                                    className="btn btn-xs d-inline-flex align-items-center gap-1 mt-1 shadow-none py-0 px-2 fw-semibold"
                                    style={{
                                      fontSize: '0.68rem',
                                      borderRadius: '4px',
                                      backgroundColor: isDark ? '#1e293b' : '#f3e8ff',
                                      color: isDark ? '#c084fc' : '#7e22ce',
                                      border: isDark ? '1px solid #7e22ce' : '1px solid #d8b4fe',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => onOpenCombinedMessages && onOpenCombinedMessages(srcTicketId)}
                                    title={`Ver la conversación transferida del ticket #${srcTicketId}`}
                                  >
                                    <FontAwesomeIcon icon={faComments} style={{ fontSize: '0.65rem' }} />
                                    Ver conversación #{srcTicketId}
                                  </button>
                                );
                              })()}
                              {ev.vCreatedUser && (
                                <div className="text-muted" style={{ fontSize: '0.65rem', opacity: 0.8 }}>
                                  Por: {ev.vCreatedUser}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Enlace para abrir chat si no es el ticket actual */}
                    {!isCurrent && (
                      <div className="mt-2 pt-2 border-top text-center" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
                        <a
                          href={`/support-engine/ticket/${item.idTicket}`}
                          className="text-primary text-decoration-none fw-semibold"
                          style={{ fontSize: '0.72rem' }}
                        >
                          Abrir conversación de este ticket →
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SidebarTicketHistory;
